import * as moment from 'moment';
import {
    IStation,
    IImomoResponse,
    IImomoResponsePaginated,
    IPrecipitationNorm,
    ITemperatureNorm,
    IAggregationsData,
    IStationCheck,
    IStationTelegramCheck,
    IForecast,
    IForecastModel,
    IForecastType,
    IForecastResultManual,
    ISnowDataResponse,
    ISnowData,
    ISnowDataNorm,
    ISnowDataFile
} from './api.types';
import {JSON_MIME_TYPE} from '../utils/extensions';

export interface IStationApi {
  // discharge
  createDischargeStation(stationDetailsRequest:IStationDetailsRequest,
                dischargeModelRequest:IDischargeModelStationRequest,
                historicalDataFile:File,
                maxDischarge?: number):angular.IPromise<IStation>;
  deleteDischargeStation(stationId: number):ng.IPromise<void>;
  getDischargeStations():ng.IPromise<IStation[]>;
  searchDischargeStations(searchText: string):ng.IPromise<IStation[]>
  getPaginatedDischargeStations(pageSize: number, offset: number, orderBy: string):ng.IPromise<IImomoResponsePaginated<IStation>>
  getDischargeStationsWithTelegramByDate(date: moment.Moment, hasTelegram: boolean):ng.IPromise<IStationTelegramCheck[]>
  getDischargeStationsWithForecast():ng.IPromise<IStation[]>;
  getDischargeStationsWithAcceptedForecast():ng.IPromise<IStation[]> 
  getDischargeStation(args:{stationCode?:string, stationId?:number}):ng.IPromise<IStation>;
  isDischargeStationCodeUnique(stationCode:string, isVirtual: boolean):ng.IPromise<IStationCheck>;
  getStationHistoricDischargeDataXLSX(stationId:number):ng.IPromise<void>;
  // getStationHistoricDischargeDataUrl(stationId:number):ng.IPromise<string>;
  uploadHistoricDischargeData(stationId:number, file:File):ng.IPromise<void>;
  validateDischargeData(file:File):ng.IPromise<void>;
  updateDischargeStation(stationId:number,
                stationUpdateDetailsRequest:IStationDetailsUpdateRequest,
                maxDischarge?: number):ng.IPromise<void>;
  saveForecastModel(stationId: number, forecastModel: IForecastModel):angular.IPromise<IForecastModel>
  getForecastModels(stationId: number): ng.IPromise<IForecastModel[]>;
  getForecastModel(stationId: number, forecastModelId: number): ng.IPromise<IForecastModel>;
  deleteForecastModel(stationId: number, forecastModelId: number):ng.IPromise<void>;
  trainForecastModel(stationId: number, forecastModelId: number):ng.IPromise<IForecastModel>;
  makeForecastPrediction(stationId: number, forecastModelId: number, date: Date):ng.IPromise<IForecastModel>;
  cancelForecastModelTraining(stationId: number, forecastModelId: number):ng.IPromise<IForecastModel>;
  getForecasts(stationId: number, frequency?: string, date?: Date, previous?: boolean):ng.IPromise<IForecast[]>;
  acceptForecast(stationId: number, forecastId: number):ng.IPromise<void>;
  uploadSnowData(stationId:number, geojson:string[] | string):ng.IPromise<void>;
  getSnowData(stationId:number): ng.IPromise<ISnowData>;
  getSnowDataFile(stationId:number, year: number): ng.IPromise<ISnowDataFile>;
  getCatchmentAreaGeojson(stationId:number, inResonse: boolean): ng.IPromise<any>;
  getSnowCoverGeotiff(stationId:number): ng.IPromise<ISnowDataFile>;
  getSnowDataNorm(stationId: number): ng.IPromise<ISnowDataNorm>;
  deleteCatchmentArea(stationId: number):ng.IPromise<void>;
  createManualForecast(stationId:number, forecastResult: IForecastResultManual):ng.IPromise<void>;

  // meteo
  createMeteoStation(stationDetailsRequest:IStationDetailsRequest,
                     historicalDataFile:File):ng.IPromise<any>;
  deleteMeteoStation(stationId: number):ng.IPromise<void>;
  getMeteoStations():ng.IPromise<IStation[]>;
  searchMeteoStations(searchText: string):ng.IPromise<IStation[]>
  getPaginatedMeteoStations(pageSize: number, offset: number, orderBy: string):ng.IPromise<IImomoResponsePaginated<IStation>>;
  getMeteoStationsWithTelegramByDate(date: moment.Moment, hasTelegram: boolean):ng.IPromise<IStationTelegramCheck[]>
  getMeteoStation(args:{stationCode?:string, stationId?:number}):ng.IPromise<IStation>;
  isMeteoStationCodeUnique(stationCode:string):ng.IPromise<IStationCheck>;
  getStationHistoricMeteoDataUrl(stationId:number):ng.IPromise<string>;
  getStationHistoricMeteoDataXLSX(stationId:number):ng.IPromise<void>;
  uploadHistoricMeteoData(stationId:number, file:File):ng.IPromise<void>;
  validateMeteoData(file:File):ng.IPromise<void>;
  updateMeteoStation(stationId:number,
                stationUpdateDetailsRequest:IStationDetailsUpdateRequest):ng.IPromise<void>;
}

export interface IStationDetailsRequest {
  basin:string,
  country:string,
  latitude:number,
  longitude:number,
  region:string,
  siteCode:string,
  siteName:string,
  aggregations?: IAggregationsData[],
  isVirtual?: boolean
}

export interface IStationDetailsUpdateRequest extends IStationDetailsRequest {
  id:number;
  //safeDischarge?:number;
}

export interface IDischargeModelStationRequest {
  modelName:string,
  paramA:number,
  paramB:number,
  paramC:number,
  paramDeltaLevel:number,
  validFrom:number
}

export default class StationApi implements IStationApi {
  static $inject = ['$http', '$q', 'backendUrl'];

  private dischargeSitesEndpoint:string;
  private meteoSitesEndpoint:string;

  constructor(private $http:ng.IHttpService,
              private $q:ng.IQService, private backendUrl:string) {
    this.dischargeSitesEndpoint = `${this.backendUrl}/discharge_sites`;
    this.meteoSitesEndpoint = `${this.backendUrl}/meteo_sites`;
  }

  createDischargeStation(stationDetailsRequest: IStationDetailsRequest,
                       dischargeModelRequest: IDischargeModelStationRequest,
                       historicalDataFile: File,
                       maxDischarge?: number): angular.IPromise<IStation> {

  let deferred = this.$q.defer();
  const reader = new FileReader();

  // Check if historicalDataFile is defined
  if (historicalDataFile) {
    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      const fileContent = new Uint8Array(arrayBuffer);
      this.$http<IImomoResponse<IStation>>({
        url: this.dischargeSitesEndpoint,
        method: 'POST',
        data: {
          siteDetails: stationDetailsRequest,
          dischargeModel: dischargeModelRequest,
          historicalDataFileContent: fileContent,
          maxDischarge: maxDischarge
        }
      }).then((httpData) => {
        return httpData.data.resources[0];
      }).then(
        (response) => {
          deferred.resolve(response);
        }
      );
    };

    reader.onerror = error => {
      deferred.reject(error); // Reject the deferred if there's an error reading the file
    };
    reader.readAsArrayBuffer(historicalDataFile);
  } else {
    // If historicalDataFile is undefined, proceed without attaching file content
    this.$http<IImomoResponse<IStation>>({
      url: this.dischargeSitesEndpoint,
      method: 'POST',
      data: {
        siteDetails: stationDetailsRequest,
        dischargeModel: dischargeModelRequest,
        maxDischarge: maxDischarge
      }
    }).then((httpData) => {
      return httpData.data.resources[0];
    }).then(
      (response) => {
        deferred.resolve(response);
      }
    );
  }

  return deferred.promise;
}

  deleteDischargeStation(stationId: number):ng.IPromise<void> {
    return this.$http({
      url: `${this.dischargeSitesEndpoint}/${stationId}`,
      method: 'DELETE'
    }).then((httpData) => {
    });
  }

  createMeteoStation(stationDetailsRequest: IStationDetailsRequest,
                     historicalDataFile?: File): ng.IPromise<any> {
    let deferred = this.$q.defer();
    const reader = new FileReader();

    // Check if historicalDataFile is defined
    if (historicalDataFile) {
      reader.onload = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const fileContent = new Uint8Array(arrayBuffer);
        this.$http({
          url: this.meteoSitesEndpoint,
          method: 'POST',
          data: {
            siteDetails: stationDetailsRequest,
            historicalDataFileContent: fileContent
          }
        }).then((httpData) => {
          return httpData.data;
        }).then(
          (response) => {
            deferred.resolve(response);
          }
        );
      };

      reader.onerror = error => {
        deferred.reject(error); // Reject the deferred if there's an error reading the file
      };

      reader.readAsArrayBuffer(historicalDataFile);
    } else {
      // If historicalDataFile is undefined, proceed without attaching file content
      this.$http({
        url: this.meteoSitesEndpoint,
        method: 'POST',
        data: {
          siteDetails: stationDetailsRequest
        }
      }).then((httpData) => {
        return httpData.data;
      }).then(
        (response) => {
          deferred.resolve(response);
        }
      );
    }

    return deferred.promise;
  }

  deleteMeteoStation(stationId: number):ng.IPromise<void> {
    return this.$http({
      url: `${this.meteoSitesEndpoint}/${stationId}`,
      method: 'DELETE'
    }).then((httpData) => {
    });
  }

  getDischargeStationsWithForecast():ng.IPromise<IStation[]> {
    return this.$http<IImomoResponse<IStation>>({
      url: this.dischargeSitesEndpoint,
      method: 'GET',
      params: {
        hasForecastResult: true
      }
    }).then((httpData) => {
      return httpData.data.resources;
    });
  }

  getDischargeStationsWithAcceptedForecast():ng.IPromise<IStation[]> {
    return this.$http<IImomoResponse<IStation>>({
      url: this.dischargeSitesEndpoint,
      method: 'GET',
      params: {
        hasAcceptedForecastResult: true
      }
    }).then((httpData) => {
      return httpData.data.resources;
    });
  }

  searchDischargeStations(searchText: string):ng.IPromise<IStation[]> {
    return this.$http<IImomoResponse<IStation>>({
      url: this.dischargeSitesEndpoint,
      method: 'GET',
      params: {
        search: searchText
      }
    }).then((httpData) => {
      return httpData.data.resources;
    });
  }

  searchMeteoStations(searchText: string):ng.IPromise<IStation[]> {
    return this.$http<IImomoResponse<IStation>>({
      url: this.meteoSitesEndpoint,
      method: 'GET',
      params: {
        search: searchText
      }
    }).then((httpData) => {
      return httpData.data.resources;
    });
  }

  getDischargeStations():ng.IPromise<IStation[]> {
    return this.$http<IImomoResponse<IStation>>({
      url: this.dischargeSitesEndpoint,
      method: 'GET'
    }).then((httpData) => {
      return httpData.data.resources;
    });
  }

  getPaginatedDischargeStations(pageSize: number, offset: number, orderBy: string):ng.IPromise<IImomoResponsePaginated<IStation>> {
    return this.$http<IImomoResponsePaginated<IStation>>({
      url: this.dischargeSitesEndpoint,
      method: 'GET',
      params: {
        paginate: true,
        page_size: pageSize,
        offset,
        order_by: orderBy
      }
    }).then((httpData) => {
      return httpData.data;
    });
  }

  getMeteoStations():ng.IPromise<IStation[]> {
    return this.$http<IImomoResponse<IStation>>({
      url: this.meteoSitesEndpoint,
      method: 'GET'
    }).then((httpData) => {
      return httpData.data.resources;
    })
  }

  getPaginatedMeteoStations(pageSize: number, offset: number, orderBy: string):ng.IPromise<IImomoResponsePaginated<IStation>> {
    return this.$http<IImomoResponsePaginated<IStation>>({
      url: this.meteoSitesEndpoint,
      method: 'GET',
      params: {
        paginate: true,
        page_size: pageSize,
        offset,
        order_by: orderBy
      }
    }).then((httpData) => {
      return httpData.data;
    });
  }

  getDischargeStationsWithTelegramByDate(date: moment.Moment, hasTelegram: boolean):ng.IPromise<IStationTelegramCheck[]> {
    return this.$http<IImomoResponse<IStationTelegramCheck>>({
      url: `${this.dischargeSitesEndpoint}/check_telegram`,
      method: 'GET',
      params: {
        date: date.format(),
        hasTelegram: hasTelegram
      }
    }).then((httpData) => {
      return httpData.data.resources;
    })
  }

  getMeteoStationsWithTelegramByDate(date: moment.Moment, hasTelegram: boolean):ng.IPromise<IStationTelegramCheck[]> {
    return this.$http<IImomoResponse<IStationTelegramCheck>>({
      url: `${this.meteoSitesEndpoint}/check_telegram`,
      method: 'GET',
      params: {
        date: date.format(),
        hasTelegram: hasTelegram
      }
    }).then((httpData) => {
      return httpData.data.resources;
    })
  }

  getDischargeStation({stationCode, stationId}:{
    stationCode?:string,
    stationId?:number
  }):ng.IPromise<IStation> {
    let promise:ng.IHttpPromise<IImomoResponse<IStation>>;
    if (stationCode) {
      promise = this.$http({
        url: this.dischargeSitesEndpoint,
        method: 'GET',
        params: {
          siteCode: stationCode
        }
      });
    } else if (stationId) {
      promise = this.$http<IImomoResponse<IStation>>({
        url: `${this.dischargeSitesEndpoint}/${stationId}`,
        method: 'GET'
      });
    }
    return promise.then(httpData => {
      return httpData.data.resources[0];
    });
  }

  getMeteoStation({stationCode, stationId}:{
    stationCode?:string,
    stationId?:number
  }):ng.IPromise<IStation> {
    let promise:ng.IHttpPromise<IImomoResponse<IStation>>;
    if (stationCode) {
      promise = this.$http({
        url: this.meteoSitesEndpoint,
        method: 'GET',
        params: {
          siteCode: stationCode
        }
      });
    } else if (stationId) {
      promise = this.$http<IImomoResponse<IStation>>({
        url: `${this.meteoSitesEndpoint}/${stationId}`,
        method: 'GET'
      });
    }
    return promise.then(httpData => {
      return httpData.data.resources[0];
    });
  }

  isDischargeStationCodeUnique(stationCode:string, isVirtual: boolean):ng.IPromise<IStationCheck> {
    return this.$http({
      url: `${this.dischargeSitesEndpoint}/check`,
      method: 'POST',
      data: {
        siteCode: stationCode,
        isVirtual: isVirtual
      }
    }).then((httpData) => {
      return httpData.data;
    }).catch((errData) => {
      return errData;
    });
  }

  isMeteoStationCodeUnique(stationCode:string):ng.IPromise<IStationCheck> {
    return this.$http({
      url: `${this.meteoSitesEndpoint}/check`,
      method: 'POST',
      data: {
        siteCode: stationCode
      }
    }).then((httpData) => {
      return httpData.data;
    }).catch((errData) => {
      return errData;
    });
  }

  getStationHistoricDischargeDataXLSX(stationId:number):ng.IPromise<void> {
    return this.$http<IImomoResponse<{url:string}>>({
      url: `${this.dischargeSitesEndpoint}/${stationId}/historic_data_file`,
      method: 'GET'
    }).then(function(response: any) {
      const byteArray = new Uint8Array(response.data.fileContent);
      const filename = response.data.filename;
      var blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      // Create a link element and click it to trigger the download
      var downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.download = filename;
      downloadLink.click();
    }, function(error) {
      // Handle error
      console.error('Error downloading Excel file:', error);
    });
  }

  getStationHistoricMeteoDataXLSX(stationId:number):ng.IPromise<void> {
    return this.$http<IImomoResponse<{url:string}>>({
      url: `${this.meteoSitesEndpoint}/${stationId}/historic_data_file`,
      method: 'GET'
    }).then(function(response: any) {
      const byteArray = new Uint8Array(response.data.fileContent);
      const filename = response.data.filename;
      var blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      // Create a link element and click it to trigger the download
      var downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.download = filename;
      downloadLink.click();
    }, function(error) {
      // Handle error
      console.error('Error downloading Excel file:', error);
    });
  }

  getStationHistoricMeteoDataUrl(stationId:number):ng.IPromise<string> {
    return this.$http<IImomoResponse<{url:string}>>({
      url: `${this.meteoSitesEndpoint}/${stationId}/historic_data_file`,
      method: 'GET'
    }).then(httpData => {
      return httpData.data.resources[0].url;
    });
  }

  getForecast(stationId: number):ng.IPromise<IForecast[]>  {
    return this.$http<IImomoResponse<IForecast>>({
      url: `${this.dischargeSitesEndpoint}/${stationId}/forecast`,
      method: 'GET'
    }).then(httpData => {
      return httpData.data.resources;
    });
  }


  postFileContent(file: File, url: string):  ng.IPromise<void>{
      const deferred = this.$q.defer<void>();
      const reader = new FileReader();

      reader.onload = () => {
          const arrayBuffer = reader.result as ArrayBuffer;
          const fileContent = new Uint8Array(arrayBuffer);

          this.$http({
              url: url,
              method: 'POST',
              data: {
                  fileContent: fileContent
              }
          }).then(() => {
              deferred.resolve(); // Resolve the deferred when upload is successful
          }).catch(error => {
              deferred.reject(error); // Reject the deferred if there's an error during HTTP request
          });
      };

      reader.onerror = error => {
          deferred.reject(error); // Reject the deferred if there's an error reading the file
      };

      reader.readAsArrayBuffer(file);

      return deferred.promise;

  }

  uploadHistoricDischargeData(stationId: number, file: File): ng.IPromise<void> {
    return this.postFileContent(file, `${this.dischargeSitesEndpoint}/${stationId}/historic_data_file`);
}

  uploadHistoricMeteoData(stationId: number, file: File): ng.IPromise<void> {
    return this.postFileContent(file, `${this.meteoSitesEndpoint}/${stationId}/historic_data_file`);
}

  validateDischargeData(file:File): ng.IPromise<void> {
    return this.postFileContent(file, `${this.dischargeSitesEndpoint}/validate_historic_data_file` );
  }

  validateMeteoData(file:File): ng.IPromise<void> {
    return this.postFileContent(file, `${this.meteoSitesEndpoint}/validate_historic_data_file` );
  }

  updateDischargeStation(stationId:number,
                stationUpdateDetailsRequest:IStationDetailsUpdateRequest,
                maxDischarge?: number) {
    return this.$http({
      url: `${this.dischargeSitesEndpoint}/${stationId}`,
      method: 'PUT',
      data: {
        siteDetails: stationUpdateDetailsRequest,
        maxDischarge: maxDischarge
      }
    }).then(() => {
    });
  }

  updateMeteoStation(stationId:number,
                stationUpdateDetailsRequest:IStationDetailsUpdateRequest) {
    return this.$http({
      url: `${this.meteoSitesEndpoint}/${stationId}`,
      method: 'PUT',
      data: {
        siteDetails: stationUpdateDetailsRequest
      }
    }).then(() => {
    });
  }

  saveForecastModel(stationId: number, forecastModel: IForecastModel):ng.IPromise<IForecastModel> {
    return this.$http<IImomoResponse<IForecastModel>>({
      url: `${this.dischargeSitesEndpoint}/${stationId}/forecast_models` + (forecastModel.id || forecastModel.id === 0 ? `/${forecastModel.id}`: ''),
      method: forecastModel.id || forecastModel.id === 0 ? 'PUT':'POST',
      data: forecastModel
    }).then((httpData) => {
      return httpData.data.resources[0]
    });
  }

  getForecastModels(stationId: number): ng.IPromise<IForecastModel[]> {
    return this.$http<IImomoResponse<IForecastModel>>({
      url: `${this.dischargeSitesEndpoint}/${stationId}/forecast_models`,
      method: 'GET'
    }).then(httpData => {
      return httpData.data.resources;
    });
  }

  getForecastModel(stationId: number, forecastModelId: number): ng.IPromise<IForecastModel> {
    return this.$http<IImomoResponse<IForecastModel>>({
      url: `${this.dischargeSitesEndpoint}/${stationId}/forecast_models/${forecastModelId}`,
      method: 'GET'
    }).then((httpData) => {
      return httpData.data.resources[0]
    });
  }

  deleteForecastModel(stationId: number, forecastModelId: number):ng.IPromise<void> {
    return this.$http({
      url: `${this.dischargeSitesEndpoint}/${stationId}/forecast_models/${forecastModelId}`,
      method: 'DELETE'
    }).then((httpData) => {
    });
  }

  trainForecastModel(stationId: number, forecastModelId: number):ng.IPromise<IForecastModel> {
    return this.$http<IImomoResponse<IForecastModel>>({
      url: `${this.dischargeSitesEndpoint}/${stationId}/forecast_models/${forecastModelId}/train`,
      method: 'POST',
    }).then((httpData) => {
      return httpData.data.resources[0]
    });
  }

  makeForecastPrediction(stationId: number, forecastModelId: number, date: Date):ng.IPromise<IForecastModel> {
    return this.$http<IImomoResponse<IForecastModel>>({
      url: `${this.dischargeSitesEndpoint}/${stationId}/forecast_models/${forecastModelId}/predict`,
      method: 'POST',
      data: {
        date: date
      }
    }).then((httpData) => {
      return httpData.data.resources[0]
    });
  }

  cancelForecastModelTraining(stationId: number, forecastModelId: number):ng.IPromise<IForecastModel> {
    return this.$http<IImomoResponse<IForecastModel>>({
      url: `${this.dischargeSitesEndpoint}/${stationId}/forecast_models/${forecastModelId}/cancel`,
      method: 'POST',
    }).then((httpData) => {
      return httpData.data.resources[0]
    });
  }

  acceptForecast(stationId: number, forecastId: number):angular.IPromise<void> {
    return this.$http({
      url: `${this.dischargeSitesEndpoint}/${stationId}/forecasts/${forecastId}/accept`,
      method: 'POST',
    }).then((httpData) => {
    });
  }

  getForecasts(stationId: number, frequency?: string, date?: Date, previous?: boolean):ng.IPromise<IForecast[]>  {
    return this.$http<IImomoResponse<{frequencies: IForecast[]}>>({
      url: `${this.dischargeSitesEndpoint}/${stationId}/forecasts`,
      method: 'GET',
      params: {
        date,
        frequency,
        previous
      }
    }).then(httpData => {
      return httpData.data.resources[0].frequencies;
    });
  }

  uploadSnowData(stationId:number, geojson:string[] | string) {
    return this.$http({
      url: `${this.dischargeSitesEndpoint}/${stationId}/catchment_area`,
      method: 'POST',
      data: {
        geojson: geojson,
      }
    }).then(() => {
    });
  }

  deleteCatchmentArea(stationId: number) {
    return this.$http({
      url: `${this.dischargeSitesEndpoint}/${stationId}/catchment_area`,
      method: 'DELETE'
    }).then(() => {
    });
  }

  getSnowData(stationId:number) {
     return this.$http<ISnowDataResponse>({
       url: `${this.dischargeSitesEndpoint}/${stationId}/snow_data`,
       method: 'GET'
     }).then((httpData) => {
       return httpData.data.resources[0];
     });
  }

  getSnowDataFile(stationId:number, year: number) {
    return this.$http<IImomoResponse<ISnowDataFile>>({
      url: `${this.dischargeSitesEndpoint}/${stationId}/snow_data_file`,
      method: 'GET',
      params: {
        year: year
      }
    }).then((httpData) => {
      return httpData.data.resources[0];
    });
  }

  getCatchmentAreaGeojson(stationId:number, inResponse: boolean) {
    return this.$http<IImomoResponse<ISnowDataFile>>({
      url: `${this.dischargeSitesEndpoint}/${stationId}/catchment_area_geojson`,
      method: 'GET',
      params: {
        in_response: inResponse
      }
    }).then((httpData) => {
      return httpData.data.resources[0];
    });
  }

  getSnowCoverGeotiff(stationId:number) {
     return this.$http<IImomoResponse<ISnowDataFile>>({
       url: `${this.dischargeSitesEndpoint}/${stationId}/snow_cover_geotiff`,
       method: 'GET'
     }).then((httpData) => {
       return httpData.data.resources && httpData.data.resources[0];
     });
  }
  getSnowDataNorm(stationId: number) {
    return this.$http<IImomoResponse<any>>({
      url: `${this.dischargeSitesEndpoint}/${stationId}/snow_data_norm`,
      method: 'GET'
    }).then((httpData) => {
      return httpData.data.resources[0].resources[0];
    });
  }

  createManualForecast(stationId:number, forecastResult: IForecastResultManual) {
    return this.$http({
      url: `${this.dischargeSitesEndpoint}/${stationId}/manual_forecast`,
      method: 'POST',
      data: {
        forecastResult: forecastResult,
      }
    }).then(() => {
    });
  }
}



/** WEBPACK FOOTER **
 ** ./app/api/station-api.service.ts
 **/